import { useState } from "react";
import { Search } from "lucide-react";

// Ice blue palette, kept clear of META's saturated blue and PMAX's lime/cyan
export const ICE = "#b9dcff";

export const fmt = (v) => Number(v || 0).toLocaleString();

export const GadsHeader = ({ dateRange, clicks }) => (
  <div className="flex items-center gap-4">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#e3f0ff] to-[#7fa8e0] flex items-center justify-center flex-shrink-0 shadow-[0_0_24px_rgba(185,220,255,0.25)]">
      <Search className="w-7 h-7 text-[#0d2240]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: ICE }}>Google Ads · Paid Search</p>
      <p className="text-white text-lg md:text-xl font-bold">Right there when people search</p>
      {dateRange && <p className="text-slate-400 text-xs mt-0.5">{dateRange}</p>}
    </div>
    <span className="hidden sm:inline px-3 py-1 text-xs font-semibold rounded-full border border-[#b9dcff]/30 bg-[#b9dcff]/10" style={{ color: ICE }} data-testid="google-ads-header-clicks">
      {fmt(clicks)} clicks
    </span>
  </div>
);

const Hero = ({ label, value, sub, testId }) => (
  <div className="relative overflow-hidden bg-[#10284a]/80 border border-[#b9dcff]/15 rounded-xl p-3.5 sm:p-4 md:p-5" data-testid={testId}>
    <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#b9dcff]/10 blur-2xl" />
    <p className="text-[10px] uppercase tracking-[0.16em] font-bold mb-1 text-[#b9dcff]/70">{label}</p>
    <p className="text-white text-2xl sm:text-3xl md:text-4xl font-bold tabular-nums">{value}</p>
    {sub && <p className="text-slate-400 text-xs mt-1">{sub}</p>}
  </div>
);

export const HeadlineNumbers = ({ summary }) => {
  const tiles = [
    { label: "Times you showed up", value: fmt(summary.total_impressions), sub: "On Google search results", testId: "google-ads-kpi-impressions" },
    { label: "Clicks to your site", value: fmt(summary.total_clicks), sub: "People who chose you", testId: "google-ads-kpi-clicks" },
    { label: "Click rate", value: `${Number(summary.avg_ctr || 0).toFixed(2)}%`, sub: "Clicks per time shown", testId: "google-ads-kpi-ctr" },
  ];
  if (summary.total_conversions > 0) tiles.push({ label: "Actions taken", value: fmt(Math.round(summary.total_conversions)), sub: "Calls, form fills and more", testId: "google-ads-kpi-actions" });
  return (
    <div className={`grid grid-cols-2 ${tiles.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3 mb-5`}>
      {tiles.map((t) => <Hero key={t.testId} {...t} />)}
    </div>
  );
};

const chipSize = (i) => (i < 3 ? "text-sm md:text-base px-3.5 py-1.5 font-semibold" : i < 8 ? "text-sm px-3 py-1" : "text-xs px-2.5 py-1");

export const SearchTerms = ({ terms = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? terms : terms.slice(0, 12);
  return (
    <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-5 mb-5" data-testid="google-ads-search-terms">
      <div className="flex items-center gap-2 mb-1">
        <Search className="w-4 h-4" style={{ color: ICE }} />
        <p className="text-white font-semibold text-sm">What people searched to find you</p>
      </div>
      <p className="text-slate-400 text-xs mb-4">The words typed into Google right before they clicked your ad</p>
      <div className="flex flex-wrap gap-2">
        {shown.map((t, i) => (
          <span
            key={t.term}
            className={`rounded-full border inline-flex items-center gap-2 ${chipSize(i)} ${i < 3 ? "bg-[#b9dcff]/15 border-[#b9dcff]/40 text-white" : "bg-white/5 border-white/10 text-slate-200"}`}
            data-testid={`google-ads-term-${i}`}
          >
            {t.term}
            {t.clicks > 0 && <span className="text-[10px] font-bold tabular-nums" style={{ color: ICE }}>{fmt(t.clicks)}</span>}
          </span>
        ))}
      </div>
      {terms.length > 12 && (
        <button onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs font-semibold hover:opacity-80" style={{ color: ICE }} data-testid="google-ads-terms-toggle">
          {showAll ? "Show fewer" : `Show all ${terms.length}`}
        </button>
      )}
    </div>
  );
};

export const TopCampaigns = ({ campaigns }) => (
  <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-5" data-testid="google-ads-top-campaigns">
    <p className="text-white font-semibold text-sm mb-3">Campaigns doing the heavy lifting</p>
    <div className="space-y-2">
      {campaigns.map((c, i) => (
        <div key={c.id || i} className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2.5 border border-white/5" data-testid={`google-ads-campaign-${i}`}>
          <span className="font-bold text-sm w-5 tabular-nums" style={{ color: ICE }}>{i + 1}</span>
          <p className="text-white text-sm truncate flex-1 min-w-0" title={c.campaign_name}>{(c.campaign_name || "").replace(/\s*—\s*/g, ": ")}</p>
          <span className="text-white font-semibold text-sm tabular-nums shrink-0">{fmt(c.clicks)} <span className="text-slate-400 font-normal text-xs">clicks</span></span>
        </div>
      ))}
    </div>
  </div>
);
